import {PortalGhostWorld, portalCopyIsShadow} from './PortalGhostWorld.js';
import {PortalTransitVisual} from './PortalTransitVisual.js';
import {PortalContactRouter} from './portalContactRouter.js';
import {
	getPortalScreenFrame,
	PortalScreenBendFilter,
} from './PortalScreenBendFilter.js';
import {
	addScreenOverlayFilter,
	getScreenFilterArea,
	hasScreenFilterTarget,
	removeScreenOverlayFilter,
} from '../../utils/ScreenFilterStack.js';
import {
	portalNormal,
	portalTangentDistance,
	signedPortalDistance,
	transformPortalPoint,
} from './portalMath.js';
import {
	bodyVelocityAtPoint,
	endpointUsesHostFixture,
	fixtureLiesBehindPortal,
	fixtureLiesInFrontOfPortal,
	fixtureStartsInsidePortal,
	getFixturePortalEntrySide,
	isPortalHostContactPair,
	pointWithinPortalAperture,
	resolvePortalWheelCapContact,
} from './portalCollision.js';

const fixtureKey = fixture => Box2D.getPointer(fixture);
const readVec = vector => ({x: vector.get_x(), y: vector.get_y()});
const OWNERSHIP_TRANSFER_DISTANCE = 0.05;
const OWNERSHIP_TRANSFER_HYSTERESIS = 0.15;
const OWNERSHIP_TRANSFER_COOLDOWN_FRAMES = 8;
// A body straddling a portal, or a copy placed on its far side, is partly
// inside the wall the portal sits on. Box2D's manifold for that deep overlap
// can pick the wall's top or bottom face, or a tilted plank's own face, and put
// its points on the wrong side of the plane, so a host-wall contact deeper
// than a real lean against the wall face is dropped as buried geometry. Other
// static geometry keeps its deep contacts: a corner that rotated through a
// floor in one step (too fast for the broad phase to pair it for continuous
// collision) must be pushed back out, not ignored, or the body falls through.
const DEEP_HOST_CONTACT = 0.5;
const DEEP_STATIC_CONTACT = 2;
const isStaticFixture = fixture => fixture.GetBody().GetType() !== Box2D.b2_dynamicBody;

const isPortalSurfaceFixture = fixture => Boolean(fixture.GetBody().isPortalSurface);

const sourceFixtureCanPass = (candidate, fixture) => {
	if(fixture.IsSensor()) return false;
	const key = fixtureKey(fixture);
	return candidate.admittedFixtureKeys.has(key);
};

const opensHostFixture = (candidate, fixture) =>
	!fixture.GetBody()._portalTransitCandidate &&
	endpointUsesHostFixture(Box2D, candidate.source, fixture);

const mapThroughPortal = (point, source, target) => transformPortalPoint(
	point,
	readVec(source.body.GetPosition()),
	source.body.GetAngle(),
	readVec(target.body.GetPosition()),
	target.body.GetAngle(),
);

const getContactPoints = contact => {
	const manifold = contact.GetManifold();
	const pointCount = manifold.get_pointCount();
	if(!pointCount) return [];
	const worldManifold = new Box2D.b2WorldManifold();
	contact.GetWorldManifold(worldManifold);
	const points = [];
	for(let index = 0; index < pointCount; index++) {
		const point = worldManifold.get_points(index);
		const x = point.get_x();
		const y = point.get_y();
		points.push({index, x, y, separation: worldManifold.get_separations(index)});
	}
	Box2D.destroy(worldManifold);
	return points;
};

const keepContactPoints = (contact, keptIndices) => {
	const manifold = contact.GetManifold();
	const pointCount = manifold.get_pointCount();
	if(keptIndices.length === pointCount) return false;
	if(!keptIndices.length) return true;
	if(keptIndices[0] !== 0) manifold.set_points(0, manifold.get_points(keptIndices[0]));
	manifold.set_pointCount(keptIndices.length);
	return false;
};

// Clips a contact between a portal copy and a real body.
//
// A copy is either a ghost (exit-room geometry mapped behind the entrance) or
// a shadow (an emerging island mapped around the exit). A traveller that has
// crossed the entrance meets ghosts only behind the plane; the exit room meets
// a shadow only on its emerged part. The exit's own host surface is open inside
// its aperture. Islands coming through the other way are whole for each other:
// their real bodies and copies pair up so that each side is solved exactly.
const clipCopyContact = (contact, fixtureA, fixtureB) => {
	const copyIsA = Boolean(fixtureA.GetBody().isPortalGhost);
	const copyFixture = copyIsA ? fixtureA : fixtureB;
	const realFixture = copyIsA ? fixtureB : fixtureA;
	const copyBody = copyFixture.GetBody();
	const realBody = realFixture.GetBody();
	const side = copyBody._portalGhostSide;
	const other = copyBody._portalGhostOther;
	if(!side || !other || side.destroyed || other.destroyed) return true;
	if(realBody.isPortalSurface) {
		// A copy rests on the lips of its own portal like any straddling body: a
		// plank threaded through a wall portal is a see-saw on the lip, and its
		// shadow must balance on the exit lip the same way. The cross-line and
		// the volume never stop a copy, and other portals never touch it.
		const endpoint = realBody.portalEndpoint;
		if(endpoint !== side || !endpoint.capFixtureKeys?.has(Box2D.getPointer(realFixture))) return true;
		resolvePortalWheelCapContact(Box2D, contact, realFixture, copyFixture);
		return false;
	}
	const shadow = portalCopyIsShadow(copyBody);
	const copyCandidate = copyBody._portalGhostOf?._portalTransitCandidate;
	const candidate = realBody._portalTransitCandidate;
	// An island never meets its own image.
	if(candidate && shadow && candidate === copyCandidate) return true;
	if(candidate && candidate.source === side) {
		// The traveller's crossed part, behind this side, meets the far room.
		if(shadow) return false;
		if(copyFixture.IsSensor() || realFixture.IsSensor()) return false;
		// A part that went round the portal is still in this room.
		if(!candidate.admittedFixtureKeys.has(fixtureKey(realFixture))) return true;
		// A part still wholly in front of the entrance has nothing on the far
		// side yet. The exit's own host slab maps to a block in front of the
		// entrance, and without this rule its face could shove an approaching wheel.
		if(fixtureLiesInFrontOfPortal(Box2D, realFixture, side)) return true;
		const sidePosition = readVec(side.body.GetPosition());
		const sideAngle = side.body.GetAngle();
		const realOfCopy = copyFixture._portalGhostOf;
		const hostHole = Boolean(realOfCopy) && endpointUsesHostFixture(Box2D, other, realOfCopy);
		const buriedDepth = hostHole ? DEEP_HOST_CONTACT : isStaticFixture(copyFixture) ? DEEP_STATIC_CONTACT : Infinity;
		const kept = [];
		for(const point of getContactPoints(contact)) {
			// Judge by the contact midpoint. For a long fixture at a grazing angle
			// the manifold's touched features can lie far from the real crossing
			// (a floor's corner against a plank's end face); the midpoint never does.
			if(signedPortalDistance(point, sidePosition, sideAngle) > 0.01) continue;
			if(point.separation < -buriedDepth) continue;
			if(hostHole && pointWithinPortalAperture(mapThroughPortal(point, side, other), other)) continue;
			kept.push(point.index);
		}
		return keepContactPoints(contact, kept);
	}
	// Any other traveller keeps to its own sides.
	if(candidate) return true;
	// Ordinary bodies only ever meet the emerged part of a shadow, and only of
	// parts that came in through the opening.
	if(!shadow) return true;
	if(copyFixture.IsSensor() || realFixture.IsSensor()) return false;
	const original = copyFixture._portalGhostOf;
	if(copyCandidate && original && !copyCandidate.admittedFixtureKeys.has(fixtureKey(original))) return true;
	const sidePosition = readVec(side.body.GetPosition());
	const sideAngle = side.body.GetAngle();
	const hostHole = endpointUsesHostFixture(Box2D, side, realFixture);
	const buriedDepth = hostHole ? DEEP_HOST_CONTACT : isStaticFixture(realFixture) ? DEEP_STATIC_CONTACT : Infinity;
	const kept = [];
	for(const point of getContactPoints(contact)) {
		// The contact midpoint, for the same reason as above.
		if(signedPortalDistance(point, sidePosition, sideAngle) < -0.01) continue;
		if(point.separation < -buriedDepth) continue;
		if(hostHole && pointWithinPortalAperture(point, side)) continue;
		kept.push(point.index);
	}
	return keepContactPoints(contact, kept);
};

export const shouldDisablePortalContact = contact => {
	// A routed ghost contact was already clipped on its raw contact.
	if(contact.isPortalGhostView) return false;
	const fixtureA = contact.GetFixtureA();
	const fixtureB = contact.GetFixtureB();
	const bodyA = fixtureA.GetBody();
	const bodyB = fixtureB.GetBody();
	if(bodyA.isPortalGhost || bodyB.isPortalGhost) {
		// Copies of one side are a consistent picture of the other room and
		// collide among themselves unclipped; copies of different sides never meet.
		if(bodyA.isPortalGhost && bodyB.isPortalGhost) {
			return bodyA._portalGhostSide !== bodyB._portalGhostSide;
		}
		return clipCopyContact(contact, fixtureA, fixtureB);
	}
	// A shot portal must never collide with the body it is mounted on.
	if(isPortalHostContactPair(Box2D, bodyA, bodyB)) return true;
	const candidateA = bodyA._portalTransitCandidate;
	const candidateB = bodyB._portalTransitCandidate;

	// Contacts internal to the travelling island are still solved normally;
	// its original joints and self-collision rules remain authoritative.
	if(candidateA && candidateA === candidateB) return false;
	// Two islands entering the same portal share one frame on both sides of
	// its plane: their crossed parts really do meet beyond the exit, so the
	// whole contact is real.
	if(candidateA && candidateB && candidateA.source === candidateB.source) return false;
	const entries = [
		{candidate: candidateA, travellingFixture: fixtureA, otherFixture: fixtureB},
		{candidate: candidateB, travellingFixture: fixtureB, otherFixture: fixtureA},
	].filter(entry => entry.candidate);
	if(!entries.length) return false;
	const points = getContactPoints(contact);
	const keptIndices = new Set(points.map(point => point.index));
	for(const {candidate, travellingFixture, otherFixture} of entries) {
		// The portal prefab handles its own rim/backstop contacts. Sensor contacts
		// already have no physical response and need no special handling here.
		if(otherFixture.IsSensor() || isPortalSurfaceFixture(otherFixture)) continue;
		if(!sourceFixtureCanPass(candidate, travellingFixture)) continue;
		// A fully crossed fixture belongs to the other room. A large host floor
		// can still report its front face against that hidden fixture; clipping
		// only the obstacle's contact point would let it pin a trailing limb here.
		if(fixtureLiesBehindPortal(Box2D, travellingFixture, candidate.source)) return true;
		// Once ownership has transferred at least once, the island is emerging
		// rather than entering. Its parts that have fully come out may rest on
		// the host surface inside the aperture, so a rider stuck between two floor
		// portals can push against the floor and climb out instead of floating
		// at the plane forever. Before the first transfer every admitted fixture
		// keeps sinking, otherwise nothing would ever enter a floor portal.
		if(candidate.ownershipTransferCount > 0 &&
			fixtureLiesInFrontOfPortal(Box2D, travellingFixture, candidate.source)) {
			if(opensHostFixture(candidate, otherFixture)) candidate.restingOnHost = true;
			continue;
		}
		// Two separately travelling objects must continue to collide on their
		// source-side portions. Only ordinary world geometry is opened by the
		// portal aperture; traveller-vs-traveller ownership is split by the plane.
		const otherIsTransit = Boolean(otherFixture.GetBody()._portalTransitCandidate);
		const opensThroughAperture = !otherIsTransit && endpointUsesHostFixture(
			Box2D,
			candidate.source,
			otherFixture,
		);

		const sourcePosition = candidate.source.body.GetPosition();
		const sourcePoint = {x: sourcePosition.get_x(), y: sourcePosition.get_y()};
		for(const point of points) {
			// Judge each point by the contact midpoint. A deep overlap between a
			// travelling fixture and geometry it passes through is dropped by depth
			// when that geometry is static; its touched features would otherwise
			// mislead (a tilted plank's end face against a floor's far corner).
			// Open only the part of an intersecting wall/floor that lies within the
			// finite aperture. A large fixture can therefore keep supporting a wheel
			// or box outside the portal caps instead of becoming globally non-solid.
			const buriedDepth = opensThroughAperture ? DEEP_HOST_CONTACT :
				isStaticFixture(otherFixture) ? DEEP_STATIC_CONTACT : Infinity;
			const passesThroughOpening = (opensThroughAperture &&
				pointWithinPortalAperture(point, candidate.source)) ||
				point.separation < -buriedDepth;
			// Once a contact point is behind the entrance cross-line it belongs to
			// the destination: the ghost geometry there carries it instead.
			if(passesThroughOpening || signedPortalDistance(
				point,
				sourcePoint,
				candidate.source.body.GetAngle(),
			) < -0.01) {
				keptIndices.delete(point.index);
			}
		}
	}
	return keepContactPoints(contact, [...keptIndices].sort((a, b) => a - b));
};

const liveBridges = new Set();

// Whether any portal pair is linked, so rays that could pass through one need
// the portal-aware cast.
export const hasLinkedPortals = () => liveBridges.size > 0;

export const linkedPortals = () => [...liveBridges].flatMap(bridge => [bridge.endpointA, bridge.endpointB]);

export class PortalBridge {
	constructor(endpointA, endpointB, options) {
		this.endpointA = endpointA;
		this.endpointB = endpointB;
		this.options = options;
		// Portal.linkPortal is the only construction path for a bridge, so levels
		// without a linked pair never carry any portal copies.
		this.lastUpdatedFrame = -1;
		this.destroyed = false;
		liveBridges.add(this);
		this.contactRouter = new PortalContactRouter(options.Box2D, {
			dispatch: options.dispatchContactEvent,
			getFrame: options.getFrame,
			clipContact: shouldDisablePortalContact,
		});
		this.ghostWorld = new PortalGhostWorld(options.Box2D, options.world);
		this.screenBendFilter = hasScreenFilterTarget()
			? new PortalScreenBendFilter()
			: null;
		if(this.screenBendFilter) {
			addScreenOverlayFilter(this.screenBendFilter);
			this.updateScreenBend();
		}
	}

	updateScreenBend() {
		if(!this.screenBendFilter) return;
		this.screenBendFilter.setTime(
			(typeof performance !== 'undefined' ? performance.now() : Date.now()) * 0.001,
		);
		this.screenBendFilter.setPortal(0, getPortalScreenFrame(
			this.endpointA,
			this.options.renderParent,
			this.options.ptm,
		));
		this.screenBendFilter.setPortal(1, getPortalScreenFrame(
			this.endpointB,
			this.options.renderParent,
			this.options.ptm,
		));
		this.screenBendFilter.updateVisibility(getScreenFilterArea());
	}

	otherEndpoint(endpoint) {
		return endpoint === this.endpointA ? this.endpointB : this.endpointA;
	}

	beginTransit(source, seedBody, frame, seedFixture = null, frontEntryConfirmed = false) {
		if(this.destroyed || !seedBody || seedBody.destroyed) return null;
		const target = this.otherEndpoint(source);
		if(!target || target.destroyed) return null;
		const existing = seedBody._portalTransitCandidate;
		if(existing) {
			if(existing.source === source && seedFixture &&
				(existing.admittedFixtureKeys.has(fixtureKey(seedFixture)) ||
					frontEntryConfirmed ||
					getFixturePortalEntrySide(Box2D, seedFixture, source) === 'front')) {
				this.markSourceFixtureInside(existing, seedFixture);
			}
			return existing;
		}
		// Sensor overlap alone is not permission to cross. Determine the entry
		// face from the actual fixture geometry so slow limbs, offset fixtures,
		// and articulated bodies cannot open the portal from behind.
		if(seedFixture && !frontEntryConfirmed &&
			getFixturePortalEntrySide(Box2D, seedFixture, source) !== 'front') {
			return null;
		}

		const bodyPosition = seedBody.GetWorldCenter();
		const point = {x: bodyPosition.get_x(), y: bodyPosition.get_y()};
		const portalPosition = source.body.GetPosition();
		const portalPoint = {x: portalPosition.get_x(), y: portalPosition.get_y()};
		const distance = signedPortalDistance(point, portalPoint, source.body.GetAngle());
		const tangentDistance = Math.abs(portalTangentDistance(point, portalPoint, source.body.GetAngle()));
		const normal = portalNormal(source.body.GetAngle());
		const velocity = seedBody.GetLinearVelocity();
		// Judge approach relative to the entrance; a portal on a moving host has
		// a velocity of its own.
		const sourceVelocity = bodyVelocityAtPoint(source.body, point);
		const inwardVelocity = (velocity.get_x() - sourceVelocity.x) * normal.x +
			(velocity.get_y() - sourceVelocity.y) * normal.y;

		// Calls without a fixture retain a conservative body-level fallback. All
		// runtime contact paths provide the fixture and use the exact test above.
		if(!seedFixture) {
			if(distance < -source.surfaceHalfThickness - 0.1) return null;
			if(tangentDistance > source.apertureHalfLength + 1) return null;
			if(inwardVelocity > 0.01) return null;
		}

		const candidate = this.ghostWorld.createTransit(source, target, seedBody, frame);
		if(candidate && !candidate.bridge) {
			candidate.bridge = this;
			candidate.contactRouter = this.contactRouter;
			this.contactRouter.seedPrimaryContacts(this.options.world, candidate);
		}
		if(candidate && seedFixture) this.markSourceFixtureInside(candidate, seedFixture);
		if(!candidate || candidate.visual !== undefined) return candidate;
		const center = this.ghostWorld.getIslandCenter(candidate);
		candidate.previousDistance = signedPortalDistance(center, portalPoint, source.body.GetAngle());
		// Transits usually begin inside a physics callback, where no body can be
		// created. The shadow bodies the visual needs appear on the next update.
		candidate.visual = null;
		return candidate;
	}

	ensureVisual(candidate) {
		if(candidate.visual || candidate.visualFailed || candidate.shadowsPending) return;
		if(this.options.createVisual === false) return;
		try {
			candidate.visual = new PortalTransitVisual(candidate, this.options.renderParent, this.options.ptm);
		} catch(error) {
			// Physics remains valid if an unusual custom Pixi display object cannot
			// be duplicated. Keep the crossing functional and surface the visual gap.
			console.warn('Portal transit visual could not be created', error);
			candidate.visual = null;
			candidate.visualFailed = true;
		}
	}

	// Flipping a vehicle mirrors its shapes and joint anchors in place, and a
	// snapped limb or wheel removes a joint. Rebuild the island's shadows and
	// duplicates from its current structure, keeping the transit itself alive.
	rebuildCandidate(candidate) {
		candidate.visual?.destroy();
		candidate.visual = null;
		candidate.visualFailed = false;
		if(!this.ghostWorld.rebuildTransit(candidate)) {
			this.removeCandidate(candidate);
			return false;
		}
		return true;
	}

	bootstrapInitialTransits(frame = 0) {
		if(this.destroyed) return [];
		const {Box2D, world} = this.options;
		const nullPointer = Box2D.getPointer(Box2D.NULL);
		const candidates = [];

		for(const source of [this.endpointA, this.endpointB]) {
			for(let body = world.GetBodyList();
				Box2D.getPointer(body) !== nullPointer;
				body = body.GetNext()) {
				if(body.destroyed || body.isPortalSurface || body.isPortalGhost || body.isPortalShadow ||
					body.GetType() === Box2D.b2_staticBody ||
					body._portalTransitCandidate) {
					continue;
				}

				let seedFixture = null;
				for(let fixture = body.GetFixtureList();
					Box2D.getPointer(fixture) !== nullPointer;
					fixture = fixture.GetNext()) {
					if(fixtureStartsInsidePortal(Box2D, fixture, source)) {
						seedFixture = fixture;
						break;
					}
				}
				if(!seedFixture) continue;

				const candidate = this.beginTransit(
					source,
					body,
					frame,
					seedFixture,
					true,
				);
				if(!candidate || candidate.source !== source) continue;

				// BeginContact will populate the real sensor-contact set on the first
				// Step. Until then, admit every fixture in the connected island that
				// already occupies this finite portal volume.
				candidate.sourceSensorFixtureKeys.clear();
				candidate.admittedFixtureKeys.clear();
				for(const candidateBody of candidate.bodies) {
					for(let fixture = candidateBody.GetFixtureList();
						Box2D.getPointer(fixture) !== nullPointer;
						fixture = fixture.GetNext()) {
						if(fixtureStartsInsidePortal(Box2D, fixture, source)) {
							candidate.admittedFixtureKeys.add(Box2D.getPointer(fixture));
						}
					}
				}
				candidate.endedContact = true;
				candidates.push(candidate);
			}
		}
		// Level start is outside any physics step: the ghosts and shadows can be
		// built right away so the first step already sees the far side.
		this.ghostWorld.sync(frame);
		return candidates;
	}

	markSourceFixtureInside(candidate, fixture) {
		if(!candidate || !fixture) return;
		const key = fixtureKey(fixture);
		candidate.sourceSensorFixtureKeys.add(key);
		candidate.admittedFixtureKeys.add(key);
		candidate.endedContact = false;
	}

	endTransitContact(source, body, fixture = null) {
		const candidate = body?._portalTransitCandidate;
		if(!candidate || candidate.source !== source) return;
		if(fixture) candidate.sourceSensorFixtureKeys.delete(fixtureKey(fixture));
		candidate.endedContact = candidate.sourceSensorFixtureKeys.size === 0;
	}

	removeCandidate(candidate) {
		candidate.visual?.destroy();
		candidate.visual = null;
		this.contactRouter.releaseCandidate(candidate);
		this.ghostWorld.removeTransit(candidate);
	}

	rebaseCandidate(candidate, frame) {
		// The island's shadow moves to the other portal; its duplicates are
		// rebuilt from the new shadows on this same update.
		candidate.visual?.destroy();
		candidate.visual = null;
		const transferredBodies = this.ghostWorld.rebaseTransit(
			candidate,
			frame,
		);
		for(const body of transferredBodies) this.options.onBodyTeleported?.(body);
		this.options.onTransitRebased?.(candidate, transferredBodies);
		return transferredBodies;
	}

	update(timeStep, frame) {
		if(this.destroyed) return;
		// This belongs to the linked portals, not to a travelling object. Refresh
		// it even when there are no transits (and while the editor frame is
		// stationary) so the temporal vortex is continuously alive.
		this.updateScreenBend();
		if(this.lastUpdatedFrame === frame) return;
		this.lastUpdatedFrame = frame;

		// Either endpoint may ride on a moving host. Bring both up to date before
		// the ghosts are placed; otherwise the endpoint whose prefab updates later
		// is mapped one frame behind its host.
		this.endpointA?.updateHostTransform?.();
		this.endpointB?.updateHostTransform?.();

		const ghostWorld = this.ghostWorld;
		// A destroyed body (a released rope hook, a snapped part) or changed
		// shapes and joints rebuild the island; only an island with no live body
		// left is dropped.
		for(const candidate of [...ghostWorld.transits]) {
			if(ghostWorld.transitStructureChanged(candidate)) this.rebuildCandidate(candidate);
		}

		const candidates = [...ghostWorld.transits];
		for(const candidate of candidates) {
			if(!ghostWorld.transits.has(candidate)) continue;
			// Parts that went round the portal do not carry the island through it.
			const distance = ghostWorld.getIslandEntryDistance(candidate);
			const distanceRange = ghostWorld.getIslandPortalDistanceRange(candidate);

			// Move the authoritative connected island to the destination as soon as
			// its mass centre crosses the plane. The shadows then represent only the
			// remainder at the old entrance. Ordinary destination contacts, vehicle
			// logic and triggers consequently run in the destination frame while a
			// long fixture is still visibly split.
			// A contact solve can move a resting centre a few centimetres either side
			// of the plane. After a handoff, require a clear excursion before allowing
			// the reverse handoff so the two canonical frames cannot chatter.
			if(!candidate.ownershipTransferArmed &&
				distance > OWNERSHIP_TRANSFER_HYSTERESIS) {
				candidate.ownershipTransferArmed = true;
			}
			const transferDistance = candidate.ownershipTransferArmed ?
				OWNERSHIP_TRANSFER_DISTANCE : OWNERSHIP_TRANSFER_HYSTERESIS;
			const canTransferOwnership = frame >
				(candidate.lastOwnershipTransferFrame ?? -Infinity) +
				OWNERSHIP_TRANSFER_COOLDOWN_FRAMES;
			// An island resting on the host surface after emerging is stable there.
			// Its hanging remainder pulls the mass centre behind the plane, which
			// would otherwise hand ownership back and forth every cooldown, each
			// side dropping the part that had just found support. Keep the side
			// that carries at least half of the mass, and always the side holding
			// the part the player can drive: a rider resting on the floor with the
			// bike dangling on the far side hands ownership over so the bike lands
			// on its own floor and can be driven out.
			const restingOnHost = Boolean(candidate.restingOnHost);
			candidate.restingOnHost = false;
			const prefersBody = this.options.prefersRestingBody;
			const preferred = prefersBody ?
				ghostWorld.getIslandSidePresence(candidate, prefersBody) :
				{front: false, back: false};
			// Preference only steers an island that has emerged at least once; an
			// island still entering follows its mass centre so it keeps sinking.
			const hasEmerged = candidate.ownershipTransferCount > 0;
			const wantsPreferredSide = hasEmerged && restingOnHost &&
				preferred.back && !preferred.front;
			const holdsSide = (hasEmerged && preferred.front) || (restingOnHost &&
				ghostWorld.getIslandFrontMassFraction(candidate) >= 0.5);
			const shouldTransfer = canTransferOwnership && !holdsSide &&
				(distance < -transferDistance || wantsPreferredSide);
			if(shouldTransfer) {
				this.rebaseCandidate(candidate, frame);
				const rebasedCenter = ghostWorld.getIslandCenter(candidate);
				const rebasedSourcePosition = candidate.source.body.GetPosition();
				candidate.previousDistance = signedPortalDistance(
					rebasedCenter,
					{
						x: rebasedSourcePosition.get_x(),
						y: rebasedSourcePosition.get_y(),
					},
					candidate.source.body.GetAngle(),
				);
				continue;
			}

			// A wheel resting on the floor can leave the opening sideways without
			// rising the full sensor depth above it. Match the plane's contact tolerance.
			const backedOut = candidate.endedContact &&
				distanceRange.min >= -0.02;
			const fullyOnAuthoritativeSide = candidate.ownershipTransferCount > 0 &&
				frame > candidate.lastOwnershipTransferFrame + 1 &&
				distanceRange.min > candidate.source.surfaceHalfThickness;
			// A bike's centre can be outside the opening while its front wheel is
			// already crossing. Keep the split alive until the whole island clears
			// the plane; cancelling by centre position restores collisions mid-entry.
			if(backedOut || fullyOnAuthoritativeSide) {
				this.removeCandidate(candidate);
				continue;
			}
			candidate.previousDistance = distance;
		}

		for(const candidate of ghostWorld.transits) ghostWorld.holdJointsInOpening(candidate);
		// Place ghosts and shadows for the next step, then draw from them.
		ghostWorld.sync(frame);
		for(const candidate of ghostWorld.transits) {
			this.ensureVisual(candidate);
			candidate.visual?.update(timeStep);
		}
		this.contactRouter.flush(frame);
	}

	destroy() {
		if(this.destroyed) return;
		this.destroyed = true;
		liveBridges.delete(this);
		if(this.screenBendFilter) {
			removeScreenOverlayFilter(this.screenBendFilter);
			this.screenBendFilter.destroy();
			this.screenBendFilter = null;
		}
		for(const candidate of [...this.ghostWorld.transits]) {
			candidate.visual?.destroy();
			candidate.visual = null;
			this.contactRouter.releaseCandidate(candidate);
		}
		this.ghostWorld.destroy();
		this.contactRouter.destroy();
		this.endpointA = null;
		this.endpointB = null;
	}
}
